import * as turf from '@turf/turf'
import {
  GeojsonGeometryType,
  GeojsonType,
  flattenedCoordinatesFromPolygon
} from '@/utils/map/Geojson'

export const ACRES_DECIMALS = 4

export function flattenedCoordinatesFromShape (shape) {
  // GeoJSON single-polygon coordinates are nested as element [0] in a wrapper array
  if (shapeIs(shape, GeojsonGeometryType.Polygon) || shapeIs(shape, GeojsonGeometryType.MultiPolygon)) {
    const coordinates = shape?.data?.geometry?.coordinates[0]
    if (coordinates) return coordinates
  }

  return undefined
}

function typeFromShape (shape) {
  const type = shape?.data?.geometry?.type
  if (type) return GeojsonGeometryType[type]
  return undefined
}

export function shapeIs (shape, geojsonGeometryType) {
  return typeFromShape(shape)?.id === geojsonGeometryType.id
}

export function shapeIsNot (shape, geojsonGeometryType) {
  return typeFromShape(shape)?.id !== geojsonGeometryType.id
}

export function getCoordinateOnFeature (feature) {
  const pointAsFeature = turf.pointOnFeature(feature)
  const [longitude, latitude] = pointAsFeature.geometry.coordinates
  return { latitude, longitude }
}

export function getAcresFromGeojson (geojson) {
  switch (GeojsonType[geojson.type]) {
    case GeojsonType.Feature: {
      return parseFloat(getAcresFromFeature(geojson)).toFixed(ACRES_DECIMALS)
    }
    case GeojsonType.FeatureCollection: {
      return parseFloat(
        geojson.features.reduce((acc, feature) => {
          acc += parseFloat(getAcresFromFeature(feature))
          return acc
        },
        0)).toFixed(ACRES_DECIMALS)
    }
  }
}

function getAcresFromFeature (feature) {
  switch (GeojsonGeometryType[feature.geometry.type]) {
    case GeojsonGeometryType.Polygon: {
      return parseFloat(getAcresFromPolygon(feature)).toFixed(ACRES_DECIMALS)
    }
  }
  return undefined
}

function getAcresFromPolygon (polygon) {
  const coords = polygon.geometry.coordinates
  const poly = turf.polygon(coords)
  const areaInSqMeters = turf.area(poly)
  const areaInAcres = turf.convertArea(areaInSqMeters, 'meters', 'acres')
  return areaInAcres.toFixed(ACRES_DECIMALS)
}

export function intersectsSelf (shape) {
  const polygon = turf.polygon(shape.getCoordinates())
  const intersections = turf.kinks(polygon)
  return intersections.features.length > 0
}

export function overlaps (shape, allShapes) {
  const shapeAsPolygon = turf.polygon(shape.getCoordinates())
  const existingPolygons = turf.multiPolygon(allShapes
    .filter(s => s.getId() !== shape.getId())
    .map(s => s.getCoordinates()))
  return turf.booleanIntersects(shapeAsPolygon, existingPolygons)
}

export function tooComplex (shape, maxVertices) {
  const coords = flattenedCoordinatesFromShape(shape)
  return coords.length > maxVertices
}

export function simplifyShape (shape, tolerance, maxVertices) {
  const coords = shape.getCoordinates()
  const poly = turf.polygon(coords)
  const options = { tolerance: tolerance, highQuality: true }
  const simplified = turf.simplify(poly, options)
  const newCoordCount = flattenedCoordinatesFromPolygon(simplified).length
  if (newCoordCount <= maxVertices) {
    shape.data.geometry.coordinates = simplified.geometry.coordinates
  } else if (newCoordCount == null || tolerance > 1) {
    return undefined
  } else {
    simplifyShape(shape, tolerance * 10, maxVertices)
  }
}

export function pointOutsideAllPolygons ({ longitude, latitude }, polygons) {
  return polygons.every(p => {
    const type = p?.geometry?.type
    switch (GeojsonGeometryType[type]) {
      case GeojsonGeometryType.Polygon: {
        return pointOutsidePolygon({ longitude, latitude }, p.geometry.coordinates)
      }
      case GeojsonGeometryType.MultiPolygon: {
        return pointOutsideMultiPolygon({ longitude, latitude }, p.geometry.coordinates)
      }
      default: return true
    }
  })
}

function pointOutsidePolygon ({ longitude, latitude }, polygonCoordinates) {
  const point = turf.point([longitude, latitude])
  const polygon = turf.polygon(polygonCoordinates)
  return !turf.booleanContains(polygon, point)
}

function pointOutsideMultiPolygon ({ longitude, latitude }, multiPolygonCoordinates) {
  return multiPolygonCoordinates.every(c => {
    return pointOutsidePolygon({ longitude, latitude }, c)
  })
}
